/**
 * Shared types for Client Profile contacts (Contacts tab + Medical Record contact modal).
 */

export const ROI_PERMISSION_LABELS = {
  medicalHistoryAndEvaluations: 'Medical history and evaluation(s)',
  mentalHealthEvaluations: 'Mental health evaluations',
  developmentalAndSocialHistory: 'Developmental and/or social history',
  educationalRecords: 'Educational records',
  progressNotesAndTreatmentSummary: 'Progress notes, and treatment or closing summary',
  other: 'Other',
} as const;

export type RoiPermissionKey = keyof typeof ROI_PERMISSION_LABELS;

export interface ContactRoi {
  dateSigned: string;
  expirationDate?: string;
  /** Which categories of information can be disclosed */
  permissions: Partial<Record<RoiPermissionKey, boolean>>;
  otherDescription?: string;
  /** FHIR Consent resource id backing this ROI. */
  consentId?: string;
  /** FHIR Consent.status for lifecycle state (active / inactive = revoked / draft = pending). */
  consentStatus?: 'active' | 'inactive' | 'draft';
}

export const CONTACT_RELATIONSHIP_TYPES = [
  'Child',
  'Dependent',
  'Family Member',
  'Legal Guardian',
  'Physician',
  'Parent',
  'Partner',
  'Spouse',
  'Patient',
  'Other',
] as const;

export type ContactRelationshipType = (typeof CONTACT_RELATIONSHIP_TYPES)[number];

/** Storage/taxonomy used by Caseload Contacts and the profile contact modal. */
export const CONTACT_KINDS = ['emergency-contact', 'related-person', 'guarantor'] as const;
export type ContactKind = (typeof CONTACT_KINDS)[number];
export const CONTACT_KIND_LABELS: Record<ContactKind, string> = {
  'emergency-contact': 'Emergency contact',
  'related-person': 'Related Person',
  'guarantor': 'Guarantor',
};

export interface ContactPhone {
  value: string;
  type?: string;
}

/** Where this contact record is stored in FHIR. */
export type ContactSource = 'patient-contact' | 'related-person';

export interface Contact {
  id: string;
  /** FHIR storage origin — determines which resource to update on save. */
  source: ContactSource;
  /** For RelatedPerson contacts: the RelatedPerson.id (without prefix). */
  relatedPersonId?: string;
  /** Whether the underlying resource is active (RelatedPerson.active). */
  active?: boolean;
  legalFirstName: string;
  middleName?: string;
  legalLastName: string;
  suffix?: string;
  preferredName?: string;
  relationshipType: ContactRelationshipType;
  /** Emergency / Related Person / Guarantor — independent of family relationship. */
  contactKind?: ContactKind;
  isEmergencyContact: boolean;
  emails?: string[];
  phones?: ContactPhone[];
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  notes?: string;
  role?: string;
  organization?: string;
  /** Release of Information on file for this contact */
  roi?: ContactRoi;
}
